/**
 * HTTP client for rm-api (T022; D3, C6, W6, W13).
 *
 * Responsibilities (zero domain knowledge — endpoints/ owns that):
 *  - cookie auth (`credentials: 'include'`) + CSRF double-submit header
 *  - single-flight refresh-once on 401 (C6): one shared `/auth/refresh`, retry
 *    the original request once; refresh itself never retries (no loop)
 *  - tolerant Zod parse at the boundary (D3/D4)
 *  - dev-only diagnostics (W6): ZodError, refresh failures, unmapped error codes
 *
 * `createApiClient()` is a FACTORY (not a singleton) so tests inject a fetch
 * impl / CSRF reader and get an isolated `refreshPromise`. The app uses the
 * default `api` instance below.
 */
import type { ZodTypeAny, infer as ZodInfer } from 'zod'
import { ApiError, fromResponse } from './errors'
import { resolveApiOrigin } from './apiOrigin'
import { ploteaApp } from './productProfile'

const API_PREFIX = '/api/v1'
const REFRESH_PATH = '/auth/refresh'

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** Outcome of a refresh attempt — drives ApiUnavailable vs Unauthenticated (T026). */
type RefreshResult = 'ok' | 'unauthenticated' | 'unavailable'

export interface RequestOptions<S extends ZodTypeAny | undefined> {
  method?: Method
  signal?: AbortSignal
  /** JSON-serialized into the body; sets Content-Type: application/json. */
  body?: unknown
  /** Response schema. Omit for empty-body responses (returns `undefined`). */
  schema?: S
  /** Retry once after a successful refresh on 401. Default true; auth calls pass false. */
  retryOnUnauthorized?: boolean
}

/** Narrow fetch contract — the client only ever calls fetch with a string URL. */
export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>

export interface ApiClientOptions {
  /** Prefix before `/api/v1` (default ''). Tests/cutover override. */
  baseUrl?: string
  fetchImpl?: FetchLike
  /** Reads the `csrf_token` cookie. Default: browser document.cookie. */
  getCsrfToken?: () => string | null
}

const isDev = (): boolean => {
  try {
    return import.meta.env?.DEV === true
  } catch {
    return false
  }
}

function browserCsrfToken(): string | null {
  if (typeof document === 'undefined') return null
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith('csrf_token=')) {
      return trimmed.slice('csrf_token='.length)
    }
  }
  return null
}

const isAuthPath = (path: string): boolean => path.startsWith('/auth/')
const isMutating = (method: Method): boolean => method !== 'GET'

type Result<S extends ZodTypeAny | undefined> = S extends ZodTypeAny
  ? ZodInfer<S>
  : undefined

export function createApiClient(options: ApiClientOptions = {}) {
  const baseUrl = options.baseUrl ?? ''
  // Late-bound so a test/MSW that swaps globalThis.fetch after import is honored.
  const doFetch: FetchLike =
    options.fetchImpl ?? ((u, init) => globalThis.fetch(u, init))
  const getCsrfToken = options.getCsrfToken ?? browserCsrfToken

  // Single-flight: all concurrent 401s await the same in-flight refresh (C6).
  // Lives in the closure → per-client, so tests are isolated.
  let refreshPromise: Promise<RefreshResult> | null = null

  function url(path: string): string {
    return `${baseUrl}${API_PREFIX}${path}`
  }

  function buildInit(
    method: Method,
    path: string,
    body?: unknown,
  ): RequestInit {
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (isMutating(method) && !isAuthPath(path)) {
      const csrf = getCsrfToken()
      // Never send an empty header — let the caller force a getMe/refresh to set the cookie.
      if (csrf) headers['x-csrf-token'] = csrf
    }
    return {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }
  }

  async function rawFetch(
    method: Method,
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<Response> {
    return doFetch(url(path), { ...buildInit(method, path, body), signal })
  }

  /** POST /auth/refresh exactly once; classify the outcome. No retry (no loop). */
  async function doRefresh(): Promise<RefreshResult> {
    let resp: Response
    try {
      resp = await rawFetch('POST', REFRESH_PATH)
    } catch (e) {
      if (isDev()) console.warn('[api] refresh failed (network)', e)
      return 'unavailable'
    }
    if (resp.ok) return 'ok'
    if (resp.status === 401) {
      if (isDev())
        console.warn('[api] refresh rejected (401) → unauthenticated')
      return 'unauthenticated'
    }
    // 429 / 5xx → transient; do NOT log the user out (C6).
    if (isDev())
      console.warn(`[api] refresh unavailable (status ${resp.status})`)
    return 'unavailable'
  }

  function refreshOnce(): Promise<RefreshResult> {
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => {
        refreshPromise = null
      })
    }
    return refreshPromise
  }

  async function parseError(resp: Response): Promise<ApiError> {
    const bodyText = await resp.text().catch(() => '')
    const err = fromResponse(resp.status, bodyText, (code) => {
      if (isDev()) console.warn('[api] unmapped error code', code)
    })
    // Surface the server's untranslated reason (deserialize rejections, field
    // validation) — the generic Polish message alone hides what to fix.
    if (isDev() && err.detail)
      console.warn('[api] error detail', {
        status: err.status,
        detail: err.detail,
      })
    return err
  }

  async function request<S extends ZodTypeAny | undefined = undefined>(
    path: string,
    opts: RequestOptions<S> = {},
  ): Promise<Result<S>> {
    const method = opts.method ?? 'GET'
    const retry = opts.retryOnUnauthorized ?? true

    let resp: Response
    try {
      resp = await rawFetch(method, path, opts.body, opts.signal)
    } catch (e) {
      if (opts.signal?.aborted) throw e
      if (isDev()) console.error('[api] network error', { path, error: e })
      throw new ApiError('Unknown', 'Brak polaczenia z serwerem', 0)
    }

    if (resp.status === 401 && retry && !isAuthPath(path)) {
      const outcome = await refreshOnce()
      if (outcome === 'ok') {
        try {
          resp = await rawFetch(method, path, opts.body, opts.signal)
        } catch (e) {
          if (opts.signal?.aborted) throw e
          if (isDev())
            console.error('[api] network error (post-refresh)', {
              path,
              error: e,
            })
          throw new ApiError('Unknown', 'Brak polaczenia z serwerem', 0)
        }
      } else if (outcome === 'unavailable') {
        // Transient backend failure — surface as 503-ish, not a logout (T026 → ApiUnavailable).
        throw new ApiError(
          'Unknown',
          'Blad serwera. Sprobuj ponownie pozniej',
          503,
        )
      }
      // 'unauthenticated' → fall through; the still-401 response becomes Unauthorized.
    }

    if (!resp.ok) throw await parseError(resp)

    if (!opts.schema) return undefined as Result<S>

    let json: unknown
    try {
      json = await resp.json()
    } catch (e) {
      if (opts.signal?.aborted) throw e
      if (isDev()) console.error('[api] response not JSON', { path, error: e })
      throw new ApiError(
        'Unknown',
        'Nieprawidlowa odpowiedz serwera',
        resp.status,
      )
    }

    const result = opts.schema.safeParse(json)
    if (!result.success) {
      if (isDev())
        console.error('[api] schema validation failed', {
          endpoint: path,
          issues: result.error.issues,
        })
      throw new ApiError(
        'Unknown',
        'Nieprawidlowa odpowiedz serwera',
        resp.status,
      )
    }
    return result.data as Result<S>
  }

  return {
    request,
    get: <S extends ZodTypeAny | undefined = undefined>(
      path: string,
      schema?: S,
      opts?: Omit<RequestOptions<S>, 'method' | 'body' | 'schema'>,
    ) => request<S>(path, { ...opts, method: 'GET', schema }),
    post: <S extends ZodTypeAny | undefined = undefined>(
      path: string,
      body?: unknown,
      schema?: S,
      opts?: Omit<RequestOptions<S>, 'method' | 'body' | 'schema'>,
    ) => request<S>(path, { ...opts, method: 'POST', body, schema }),
    put: <S extends ZodTypeAny | undefined = undefined>(
      path: string,
      body?: unknown,
      schema?: S,
      opts?: Omit<RequestOptions<S>, 'method' | 'body' | 'schema'>,
    ) => request<S>(path, { ...opts, method: 'PUT', body, schema }),
    patch: <S extends ZodTypeAny | undefined = undefined>(
      path: string,
      body?: unknown,
      schema?: S,
      opts?: Omit<RequestOptions<S>, 'method' | 'body' | 'schema'>,
    ) => request<S>(path, { ...opts, method: 'PATCH', body, schema }),
    del: <S extends ZodTypeAny | undefined = undefined>(
      path: string,
      schema?: S,
      opts?: Omit<RequestOptions<S>, 'method' | 'body' | 'schema'>,
    ) => request<S>(path, { ...opts, method: 'DELETE', schema }),
    /** Shared single-flight refresh — useAuth's proactive refresh dedups via this (T026). */
    refresh: refreshOnce,
    /** Absolute API URL for transports outside this client (map tile loaders). */
    url,
  }
}

export type ApiClient = ReturnType<typeof createApiClient>

/**
 * Default app-wide client. Tests build their own via `createApiClient`. The
 * Plotea web app calls `api.<apex>` (see `resolveApiOrigin`).
 */
export const api = createApiClient({
  baseUrl: resolveApiOrigin(
    ploteaApp,
    typeof window === 'undefined' ? undefined : window.location,
  ),
})
